import React, { useState, useEffect, useMemo, useRef } from "react";
import { openApi } from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import "./styles/global.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo-black.png";
import defaultLogoDark from "./assets/logo.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { ThemeProvider as ThemeProviderV5, createTheme as createThemeV5 } from "@mui/material/styles";
import { ptBR as ptBRV5 } from "@mui/material/locale";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appLogoChat, setAppLogoChat] = useState(defaultLogoLight);
  const [appLogoChatDark, setAppLogoChatDark] = useState(defaultLogoDark);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSettings } = useSettings();
  const isMountedRef = useRef(true);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppLogoChat,
      setAppLogoChatDark,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appLogoChat,
      appLogoChatDark,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appLogoChat, appLogoChatDark, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: "8px",
              backgroundColor: mode === "light" ? "#BDBDBD" : "#6B7280",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "light" ? "#9E9E9E" : "#9CA3AF",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "light" ? "#F5F5F5" : "#1F2937",
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: "8px",
              backgroundColor: mode === "light" ? "#E0E0E0" : "#4B5563",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "light" ? "#BDBDBD" : "#6B7280",
            },
          },
          typography: {
            h5: {
              fontSize: "1.25rem",
              fontWeight: 800,
              color: mode === "light" ? "#1A1A2E" : "#F0F0FF",
            },
            h6: {
              fontSize: "1rem",
              fontWeight: 700,
            }
          },
          overrides: {
            MuiButton: {
              root: {
                borderRadius: "28px",
                textTransform: "none",
                fontWeight: "bold",
                height: "40px",
              },
              containedPrimary: {
                backgroundColor: "#001b3d",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#000e21",
                },
              },
            },
          },
          palette: {
            type: mode,
            // Cor primária: Azul premium no claro, Azul vibrante no escuro
            primary: {
              main: mode === "light" ? (primaryColorLight || "#001b3d") : (primaryColorDark || "#4D9CFF"),
              light: mode === "light" ? "#3B78FF" : "#7DB8FF",
              dark: mode === "light" ? "#001b3d" : "#2070CC",
              contrastText: "#FFFFFF",
            },
            secondary: { main: "#EF4444" },
            success: { main: "#10B981", light: "#34D399", dark: "#059669" },
            warning: { main: "#F59E0B", light: "#FCD34D" },
            error: { main: "#EF4444", light: "#F87171" },
            info: { main: mode === "light" ? "#001b3d" : "#4D9CFF" },
            // Cores do texto
            textPrimary: mode === "light" ? "#1A1A2E" : "#F0F0FF",
            textSecondary: mode === "light" ? "#64748B" : "#94A3B8",
            borderPrimary: mode === "light" ? "#E2E8F0" : "#2A2A3E",
            // Variantes de fundo
            dark: { main: mode === "light" ? "#1A1A2E" : "#F0F0FF" },
            light: { main: mode === "light" ? "#F8FAFF" : "#1C1C2E" },
            fontColor: mode === "light" ? "#1A1A2E" : "#E2E8F0",
            // Backgrounds
            fancyBackground: mode === "light" ? "#F4F6FB" : "#0E0E1A",
            tabHeaderBackground: mode === "light" ? "#FFFFFF" : "#14142B",
            optionsBackground: mode === "light" ? "#FFFFFF" : "#1C1C2E",
            total: mode === "light" ? "#FFFFFF" : "#1C1C2E",
            cardBackground: mode === "light" ? "#FFFFFF" : "#1C1C2E",
            sidebarBackground: mode === "light" ? (primaryColorLight || "#001b3d") : "#12122A",
            // Elementos específicos
            messageIcons: mode === "light" ? "#64748B" : "#94A3B8",
            inputBackground: mode === "light" ? "#F8FAFF" : "#1C1C2E",
            barraSuperior: mode === "light" ? (primaryColorLight || "#001b3d") : "#14142B",
            // Badges e status
            ticketBackground: mode === "light" ? "#FFFFFF" : "#1C1C2E",
            dividerColor: mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)",
          },
          shape: {
            borderRadius: 14,
          },
          shadows: [
            "none",
            "0px 1px 4px rgba(0,0,0,0.08)",
            "0px 2px 8px rgba(0,0,0,0.10)",
            "0px 4px 16px rgba(0,0,0,0.12)",
            "0px 8px 24px rgba(0,0,0,0.12)",
            "0px 12px 32px rgba(0,0,0,0.14)",
            ...Array(19).fill("0px 12px 32px rgba(0,0,0,0.14)"),
          ],
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appLogoChat,
          appLogoChatDark,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appLogoChat, appLogoChatDark, appName, locale, mode, primaryColorDark, primaryColorLight]
  );
  const themeV5 = useMemo(
    () =>
      createThemeV5(
        {
          palette: {
            mode: mode,
            primary: {
              main: mode === "light" ? (primaryColorLight || "#0057FF") : (primaryColorDark || "#4D9CFF"),
            },
            background: {
              default: mode === "light" ? "#F8FAFC" : "#020617",
              paper: mode === "light" ? "#FFFFFF" : "#0F172A",
            },
            text: {
              primary: mode === "light" ? "#1E293B" : "#F0F0FF",
              secondary: mode === "light" ? "#64748B" : "#94A3B8",
            },
            // Custom palette items for consistency
            fancyBackground: mode === "light" ? "#F4F6FB" : "#0E0E1A",
            cardBackground: mode === "light" ? "#FFFFFF" : "#0F172A",
            borderPrimary: mode === "light" ? "#E2E8F0" : "#2A2A3E",
          },
          shape: {
            borderRadius: 14,
          },
          components: {
            MuiPaper: {
              styleOverrides: {
                root: {
                  backgroundImage: 'none',
                }
              }
            }
          }
        },
        ptBRV5
      ),
    [mode, primaryColorLight, primaryColorDark]
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale =
      i18nlocale && i18nlocale.length >= 5
        ? i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5)
        : "ptBR";

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    const loadPublicBranding = async () => {
      try {
        const settings = await getPublicSettings();
        if (!isMountedRef.current) return;

        const settingsMap = (settings || []).reduce((acc, setting) => {
          if (setting?.key) {
            acc[setting.key] = setting.value;
          }
          return acc;
        }, {});

        setPrimaryColorLight(settingsMap.primaryColorLight || "#0000FF");
        setPrimaryColorDark(settingsMap.primaryColorDark || "#39ACE7");
        setAppLogoLight(settingsMap.appLogoLight ? getBackendUrl() + "/public/" + settingsMap.appLogoLight : defaultLogoLight);
        setAppLogoDark(settingsMap.appLogoDark ? getBackendUrl() + "/public/" + settingsMap.appLogoDark : defaultLogoDark);
        setAppLogoFavicon(settingsMap.appLogoFavicon ? getBackendUrl() + "/public/" + settingsMap.appLogoFavicon : defaultLogoFavicon);
        setAppLogoChat(settingsMap.appLogoChat ? getBackendUrl() + "/public/" + settingsMap.appLogoChat : null);
        setAppLogoChatDark(settingsMap.appLogoChatDark ? getBackendUrl() + "/public/" + settingsMap.appLogoChatDark : null);
        setAppName(settingsMap.appName || "Whaticket - V15.0.1");
      } catch (error) {
        if (!isMountedRef.current) return;
        setPrimaryColorLight("#0000FF");
        setPrimaryColorDark("#39ACE7");
        setAppLogoLight(defaultLogoLight);
        setAppLogoDark(defaultLogoDark);
        setAppLogoFavicon(defaultLogoFavicon);
        setAppLogoChat(null);
        setAppLogoChatDark(null);
        setAppName("Whaticket - V15.0.1");
      }
    };

    loadPublicBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await openApi.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        // falha silenciosa para não poluir o carregamento inicial
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon || defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <ThemeProviderV5 theme={themeV5}>
            <QueryClientProvider client={queryClient}>
              <ActiveMenuProvider>
                <div style={{ position: "relative", overflow: "visible", zIndex: 0, minHeight: "100vh" }}>
                  <Routes />
                </div>
              </ActiveMenuProvider>
            </QueryClientProvider>
          </ThemeProviderV5>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
